import React from 'react';
import MediaControl from '../media/MediaControl';
import { useCornerControl } from '../media/useCornerControls';

/**
 * The tended card's Media corner — the shared MediaControl in the card's
 * upper-right, opening left. The card supplies its pool position and the
 * handlers it already has: selection persists as the post's choice,
 * delete is "remove from this post" (immediate, reversible by adding it
 * back), add opens the shared Pexels / Upload picker. Hidden while the
 * recorder owns the media area. Record, Play, Mute and Fit/Fill stay where
 * they are (their own tickets).
 */
interface TendMediaControlProps {
  pool: string[];
  selectedIndex: number;
  previewUrl?: string;
  previewIsVideo?: boolean;
  visible: boolean;
  hidden?: boolean;
  busy?: boolean;
  error?: string | null;
  onSelect: (index: number) => void;
  onAdd: () => void;
  onDelete: (index: number) => void;
  /** The Preview opens the Content Media Gallery (the card renders it). */
  onPreviewClick?: () => void;
}

const TendMediaControl: React.FC<TendMediaControlProps> = ({
  pool, selectedIndex, previewUrl, previewIsVideo, visible, hidden = false, busy, error,
  onSelect, onAdd, onDelete, onPreviewClick,
}) => {
  const corner = useCornerControl('media');
  if (hidden) return null;
  const total = pool.length;
  const position = total ? Math.min(selectedIndex, total - 1) + 1 : 0;
  const step = (delta: number) => {
    if (total < 2) return;
    onSelect((selectedIndex + delta + total) % total);
  };
  return (
    <MediaControl
      anchor="top-right"
      isOpen={corner.isOpen}
      onToggle={corner.toggle}
      visible={visible}
      previewUrl={previewUrl}
      previewIsVideo={previewIsVideo}
      position={position}
      total={total}
      canDelete={total > 1}
      deleteTitle="Remove from this post"
      onDelete={(e) => { e.stopPropagation(); onDelete(selectedIndex); }}
      onAdd={onAdd}
      onPrev={() => step(-1)}
      onNext={() => step(1)}
      busy={busy}
      error={error}
      onPreviewClick={onPreviewClick}
    />
  );
};

export default TendMediaControl;
